/**
 * Shared formatting functions for TheOddsAPI widgets.
 * Extracted from odds_dashboard_with_charts.html (lines 2704-2824).
 */

/**
 * Format odds value with spread/total annotations (lines 2704-2728)
 * @param {number} value - The odds price
 * @param {string} marketType - h2h, spreads, totals, outrights
 * @param {number|null} point - Spread or total point value
 * @param {string} oddsFormat - 'american' (default) or 'decimal'
 * @returns {string} Formatted odds string
 */
export function formatOddsValue(value, marketType, point, oddsFormat = 'american') {
  if (value === undefined || value === null) return '-'

  let formatted
  if (oddsFormat === 'decimal') {
    // Convert American odds to decimal
    const decimal = value > 0 ? (value / 100) + 1 : (100 / Math.abs(value)) + 1
    formatted = decimal.toFixed(2)
  } else {
    // American odds format
    formatted = value > 0 ? `+${value}` : String(value)
  }

  if (marketType === 'spreads' && point !== undefined && point !== null) {
    const pointStr = point > 0 ? `+${point}` : String(point)
    return `${pointStr} (${formatted})`
  }

  if (marketType === 'totals' && point !== undefined && point !== null) {
    return `${point} (${formatted})`
  }

  return formatted
}

/**
 * Format bookmaker API key to display name (lines 2731-2791)
 * 58 mappings from the original dashboard.
 */
const BOOKMAKER_NAMES = {
  draftkings: 'DraftKings',
  fanduel: 'FanDuel',
  betmgm: 'BetMGM',
  caesars: 'Caesars',
  pointsbetus: 'PointsBet US',
  betrivers: 'BetRivers',
  unibet_us: 'Unibet US',
  williamhill_us: 'William Hill US',
  barstool: 'Barstool',
  wynnbet: 'WynnBET',
  foxbet: 'FOX Bet',
  superbook: 'SuperBook',
  twinspires: 'TwinSpires',
  betonlineag: 'BetOnline',
  bovada: 'Bovada',
  mybookieag: 'MyBookie',
  betus: 'BetUS',
  lowvig: 'LowVig',
  circasports: 'Circa Sports',
  skybet: 'Sky Bet',
  paddypower: 'Paddy Power',
  williamhill: 'William Hill',
  betfair: 'Betfair',
  betfair_ex_uk: 'Betfair Exchange UK',
  ladbrokes: 'Ladbrokes',
  coral: 'Coral',
  betvictor: 'BetVictor',
  betway: 'Betway',
  betfred: 'Betfred',
  sport888: '888sport',
  unibet_uk: 'Unibet UK',
  matchbook: 'Matchbook',
  betfair_sb_uk: 'Betfair SB UK',
  smarkets: 'Smarkets',
  virginbet: 'Virgin Bet',
  livescorebet: 'LiveScore Bet',
  mrgreen: 'Mr Green',
  betuk: 'Bet UK',
  spreadex: 'Spreadex',
  boylesports: 'BoyleSports',
  leovegas: 'LeoVegas',
  casumo: 'Casumo',
  grosvenor: 'Grosvenor',
  betdaq: 'BETDAQ',
  pinnacle: 'Pinnacle',
  onexbet: '1xBet',
  marathon_bet: 'Marathon Bet',
  unibet_eu: 'Unibet EU',
  nordicbet: 'NordicBet',
  suprabets: 'Suprabets',
  betsson: 'Betsson',
  betclic: 'Betclic',
  everygame: 'Everygame',
  coolbet: 'Coolbet',
  intertops: 'Intertops',
  sportsbet: 'Sportsbet',
  ladbrokes_au: 'Ladbrokes AU',
  tab: 'TAB',
  pointsbetau: 'PointsBet AU',
  neds: 'Neds',
  unibet: 'Unibet',
  betfair_ex_au: 'Betfair Exchange AU',
  bluebet: 'BlueBet',
  topbetta: 'TopBetta',
  betr_au: 'Betr AU',
  playup: 'PlayUp',
}

export function formatBookmakerName(key) {
  return BOOKMAKER_NAMES[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Format market key to display name (lines 2794-2824)
 */
const MARKET_NAMES = {
  h2h: 'Moneyline (H2H)',
  h2h_lay: 'Moneyline (LAY)',
  spreads: 'Spreads',
  totals: 'Totals (O/U)',
  outrights: 'Outrights',
  btts: 'Both Teams to Score',
  draw_no_bet: 'Draw No Bet',
  double_chance: 'Double Chance',
  alternate_spreads: 'Alternate Spreads',
  alternate_totals: 'Alternate Totals',
  player_points: 'Player Points',
  player_rebounds: 'Player Rebounds',
  player_assists: 'Player Assists',
  player_threes: 'Player Threes',
  player_blocks_steals: 'Player Blocks+Steals',
  player_pass_tds: 'Player Pass TDs',
  player_rush_yds: 'Player Rush Yards',
}

export function formatMarketName(key) {
  return MARKET_NAMES[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}
